// ──────────────────────────────────────────────────────────────────────────
// db.js — the only module the UI uses to read/write persistent state.
//
// Behaviour:
//   - If the user is signed in, all methods hit Supabase (RLS-scoped by
//     auth.uid()).
//   - If the user is a guest (no session), all methods hit the local guest
//     store under the `proxera.guest.v1` localStorage key. The guest store
//     mirrors the server shapes so migration is trivial.
//
// This mirror is intentional: the UI calls the same function regardless of
// auth state, so behaviour is identical pre- and post-sign-in. The one
// difference is that guest data is device-local; sign-in uploads it and
// then the UI seamlessly keeps going on the server-backed store.
// ──────────────────────────────────────────────────────────────────────────

import { supabase } from './supabase.js';
import { v4 as uuidv4 } from 'uuid';
import { guestStore } from './guest.js';

async function currentUserId() {
  const { data } = await supabase.auth.getUser();
  return data?.user?.id || null;
}

// ────────────────── HABITS ──────────────────
async function listHabits() {
  const uid = await currentUserId();
  if (!uid) return guestStore.listHabits();
  const { data, error } = await supabase
    .from('habits')
    .select('*')
    .is('deleted_at', null)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(mapHabitFromServer);
}

async function createHabit(input) {
  const uid = await currentUserId();
  const row = {
    id: input.id || uuidv4(),
    name: input.name,
    icon: input.icon,
    accent: input.accent,
    frequency: input.frequency || 'Daily',
    custom_days: input.customDays || null,
    sort_order: input.sortOrder ?? 0,
    streak: input.streak ?? 0,
    longest: input.longest ?? 0,
  };
  if (!uid) return guestStore.createHabit(row);

  const { data, error } = await supabase
    .from('habits')
    .insert({ ...row, user_id: uid })
    .select()
    .single();
  if (error) throw error;
  return mapHabitFromServer(data);
}

async function updateHabit(id, patch) {
  const uid = await currentUserId();
  const serverPatch = {};
  if ('name' in patch) serverPatch.name = patch.name;
  if ('icon' in patch) serverPatch.icon = patch.icon;
  if ('accent' in patch) serverPatch.accent = patch.accent;
  if ('frequency' in patch) serverPatch.frequency = patch.frequency;
  if ('customDays' in patch) serverPatch.custom_days = patch.customDays;
  if ('sortOrder' in patch) serverPatch.sort_order = patch.sortOrder;
  if ('endYear' in patch) serverPatch.end_year = patch.endYear;
  if ('endMonth' in patch) serverPatch.end_month = patch.endMonth;
  if ('streak' in patch) serverPatch.streak = patch.streak;
  if ('longest' in patch) serverPatch.longest = patch.longest;

  if (!uid) return guestStore.updateHabit(id, serverPatch);

  const { data, error } = await supabase
    .from('habits')
    .update(serverPatch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return mapHabitFromServer(data);
}

async function deleteHabit(id) {
  const uid = await currentUserId();
  if (!uid) return guestStore.deleteHabit(id);
  // Hard delete: cascades checkins via FK. If you ever need undo, switch to
  // `update({ deleted_at: new Date().toISOString() })`.
  const { error } = await supabase.from('habits').delete().eq('id', id);
  if (error) throw error;
}

async function reorderHabits(idsInOrder) {
  const uid = await currentUserId();
  if (!uid) return guestStore.reorderHabits(idsInOrder);
  // One upsert per id, batched in a single request.
  const rows = idsInOrder.map((id, i) => ({ id, user_id: uid, sort_order: i }));
  const { error } = await supabase.from('habits').upsert(rows, { onConflict: 'id' });
  if (error) throw error;
}

// ────────────────── CHECK-INS ──────────────────
// Shape returned: { habitId, date (ISO YYYY-MM-DD), status }
async function listCheckins(fromIso, toIso) {
  const uid = await currentUserId();
  if (!uid) return guestStore.listCheckins(fromIso, toIso);
  let q = supabase.from('checkins').select('habit_id, date, status');
  if (fromIso) q = q.gte('date', fromIso);
  if (toIso) q = q.lte('date', toIso);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(r => ({ habitId: r.habit_id, date: r.date, status: r.status }));
}

// Upsert — idempotent (unique habit_id,date). Safe to call many times with
// the same args; ends in a consistent state regardless of retries.
async function setCheckin(habitId, isoDate, status = 'done') {
  const uid = await currentUserId();
  if (!uid) return guestStore.setCheckin(habitId, isoDate, status);
  const { error } = await supabase
    .from('checkins')
    .upsert(
      { user_id: uid, habit_id: habitId, date: isoDate, status },
      { onConflict: 'habit_id,date', ignoreDuplicates: false }
    );
  if (error) throw error;
}

async function clearCheckin(habitId, isoDate) {
  const uid = await currentUserId();
  if (!uid) return guestStore.clearCheckin(habitId, isoDate);
  const { error } = await supabase
    .from('checkins')
    .delete()
    .eq('habit_id', habitId)
    .eq('date', isoDate);
  if (error) throw error;
}

// ────────────────── BRIEFING TASKS / META ──────────────────
async function getBriefing(isoDate) {
  const uid = await currentUserId();
  if (!uid) return guestStore.getBriefing(isoDate);

  const [{ data: tasks, error: te }, { data: meta, error: me }] = await Promise.all([
    supabase.from('briefing_tasks').select('*').eq('date', isoDate).order('sort_order'),
    supabase.from('briefing_meta').select('*').eq('date', isoDate).maybeSingle(),
  ]);
  if (te) throw te;
  if (me) throw me;
  return {
    tasks: (tasks || []).map(t => ({
      id: t.id, text: t.text, checked: t.checked, priority: t.priority, sortOrder: t.sort_order,
    })),
    focus: meta?.focus || null,
    calls: meta?.calls || [],
    date_display: meta?.date_display || null,
  };
}

async function upsertBriefing(isoDate, { tasks, focus, calls, date_display }) {
  const uid = await currentUserId();
  if (!uid) return guestStore.upsertBriefing(isoDate, { tasks, focus, calls, date_display });

  // Replace-strategy per day: delete then insert. Tasks are few (≤ 20-ish)
  // so this is cheaper than reconciling. Wrapped in Promise.all for speed.
  const { error: delErr } = await supabase.from('briefing_tasks').delete().eq('date', isoDate);
  if (delErr) throw delErr;

  if (tasks && tasks.length) {
    const rows = tasks.map((t, i) => ({
      id: t.id || uuidv4(),
      user_id: uid,
      date: isoDate,
      text: t.text,
      checked: !!t.checked,
      priority: (t.priority || 'MED').toUpperCase(),
      sort_order: i,
    }));
    const { error: insErr } = await supabase.from('briefing_tasks').insert(rows);
    if (insErr) throw insErr;
  }

  const { error: metaErr } = await supabase
    .from('briefing_meta')
    .upsert(
      {
        user_id: uid,
        date: isoDate,
        focus: focus || null,
        calls: calls || [],
        date_display: date_display || null,
      },
      { onConflict: 'user_id,date' }
    );
  if (metaErr) throw metaErr;
}

async function updateBriefingTask(id, patch) {
  const uid = await currentUserId();
  if (!uid) return guestStore.updateBriefingTask(id, patch);
  const serverPatch = {};
  if ('text' in patch) serverPatch.text = patch.text;
  if ('checked' in patch) serverPatch.checked = patch.checked;
  if ('priority' in patch) serverPatch.priority = (patch.priority || 'MED').toUpperCase();
  if ('sortOrder' in patch) serverPatch.sort_order = patch.sortOrder;
  const { error } = await supabase.from('briefing_tasks').update(serverPatch).eq('id', id);
  if (error) throw error;
}

async function deleteBriefingTask(id) {
  const uid = await currentUserId();
  if (!uid) return guestStore.deleteBriefingTask(id);
  const { error } = await supabase.from('briefing_tasks').delete().eq('id', id);
  if (error) throw error;
}

// ────────────────── GOALS ──────────────────
// The schema's `kind` column has a CHECK constraint that only allows
// 'month' | 'year'. The UI uses the friendlier 'monthly' | 'yearly'
// strings everywhere else, so we normalize at the DB boundary. Without
// this mapping, every insert fails the check constraint, the optimistic
// cache entry rolls back, and the goal "disappears" right after being added.
const toDbKind = (k) => (k === 'monthly' ? 'month' : k === 'yearly' ? 'year' : k);
const fromDbKind = (k) => (k === 'month' ? 'monthly' : k === 'year' ? 'yearly' : k);

async function listGoals() {
  const uid = await currentUserId();
  if (!uid) return guestStore.listGoals();
  const { data, error } = await supabase
    .from('goals')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []).map(g => ({
    id: g.id, kind: fromDbKind(g.kind), period: g.period, text: g.text, checked: g.checked, sortOrder: g.sort_order,
  }));
}

async function createGoal({ kind, period, text }) {
  const uid = await currentUserId();
  if (!uid) return guestStore.createGoal({ kind, period, text });
  const row = { id: uuidv4(), user_id: uid, kind: toDbKind(kind), period, text, checked: false, sort_order: 0 };
  const { data, error } = await supabase.from('goals').insert(row).select().single();
  if (error) throw error;
  return { id: data.id, kind: fromDbKind(data.kind), period: data.period, text: data.text, checked: data.checked, sortOrder: data.sort_order };
}

async function updateGoal(id, patch) {
  const uid = await currentUserId();
  if (!uid) return guestStore.updateGoal(id, patch);
  const serverPatch = {};
  if ('text' in patch) serverPatch.text = patch.text;
  if ('checked' in patch) serverPatch.checked = patch.checked;
  if ('sortOrder' in patch) serverPatch.sort_order = patch.sortOrder;
  if ('period' in patch) serverPatch.period = patch.period;
  if ('kind' in patch) serverPatch.kind = toDbKind(patch.kind);
  const { error } = await supabase.from('goals').update(serverPatch).eq('id', id);
  if (error) throw error;
}

async function deleteGoal(id) {
  const uid = await currentUserId();
  if (!uid) return guestStore.deleteGoal(id);
  const { error } = await supabase.from('goals').delete().eq('id', id);
  if (error) throw error;
}

// ────────────────── PROFILE ──────────────────
async function getProfile() {
  const uid = await currentUserId();
  if (!uid) return guestStore.getProfile();
  const { data, error } = await supabase.from('profiles').select('*').eq('id', uid).maybeSingle();
  if (error) throw error;
  return data ? { id: data.id, displayName: data.display_name, createdAt: data.created_at } : null;
}

async function updateProfile(patch) {
  const uid = await currentUserId();
  if (!uid) return guestStore.updateProfile(patch);
  const serverPatch = {};
  if ('displayName' in patch) serverPatch.display_name = patch.displayName;
  const { data, error } = await supabase
    .from('profiles')
    .update(serverPatch)
    .eq('id', uid)
    .select()
    .single();
  if (error) throw error;
  return { id: data.id, displayName: data.display_name, createdAt: data.created_at };
}

// ────────────────── Helpers ──────────────────
function mapHabitFromServer(r) {
  return {
    id: r.id,
    name: r.name,
    icon: r.icon,
    accent: r.accent,
    frequency: r.frequency,
    customDays: r.custom_days,
    sortOrder: r.sort_order,
    endYear: r.end_year,
    endMonth: r.end_month,
    streak: r.streak,
    longest: r.longest,
    createdAt: r.created_at,
  };
}

export const db = {
  listHabits, createHabit, updateHabit, deleteHabit, reorderHabits,
  listCheckins, setCheckin, clearCheckin,
  getBriefing, upsertBriefing, updateBriefingTask, deleteBriefingTask,
  listGoals, createGoal, updateGoal, deleteGoal,
  getProfile, updateProfile,
};
